// Die Untertypen des Kontaktstatus „Sonstige" in den beiden Anlage-Masken.
//
// Zwei Listen, weil die Fachlichkeit zwei ist: eine Person kann Mitarbeiter
// von Courté sein, eine Firma nicht; eine Firma kann Direktionspartner sein,
// eine Person nicht. Bis 09/2026 leitete eine Funktion beide aus EINEM
// typ-eigenen Wert ab — das trug nur, solange sich die Listen in genau einem
// Eintrag unterschieden.
//
// Zwei davon gelten für Firma UND Person und unterscheiden sich fachlich nur
// in einem Punkt, der Geld kostet: ein Vermittler oder Tippgeber KANN eine
// Provision erhalten, ein Empfehlungsgeber nicht. Deshalb steht der Hinweis
// direkt an der Option und nicht in einer Fußnote — die Verwechslung wäre
// später nur mit Aufwand zu korrigieren.
//
// Die `value`-Strings sind zugleich die Werte in `ContactStatus.status` (die
// Anlage schickt `statusName`). Sie müssen ZEICHENGENAU zu
// `apps/api/src/contactStatus.katalog.ts` passen — inklusive „é" in „Courté",
// „ü" in „fürs Unternehmen" und dem Komma in „Vermittler, Tippgeber".
// `ContactService.resolveRelations` löscht `statusName` ERSATZLOS, wenn keine
// Katalogzeile passt: der Kontakt entsteht dann ohne Status und ohne
// Fehlermeldung. Abgesichert ist das durch
// `apps/api/src/sonstigeKontaktstatus.spec.ts`.
//
// Klammerzusätze gehören ausschließlich ins `label`.

import {
  STATUS_AP_PRODUKTPARTNER,
  STATUS_AP_KOOPERATIONSPARTNER,
  STATUS_KOOPERATIONSPARTNER,
  STATUS_PRODUKTPARTNER,
} from "./produktpartnerKatalog";

/** Kann Provision erhalten. */
export const STATUS_VERMITTLER = "Vermittler, Tippgeber";
/** Erhält keine Provision. */
export const STATUS_EMPFEHLUNGSGEBER = "Empfehlungsgeber";
/**
 * Wer eine Firma leitet oder für sie einsteht.
 *
 * Der PO hat am 09.09.2026 klargestellt: „Mensch fürs Unternehmen" meint
 * dasselbe wie „Verantwortung fürs Unternehmen". Seit dem 29.09.2026 ist
 * „Verantwortung fürs Unternehmen" auch der GESPEICHERTE Wert (Migration
 * 202609290070); der alte Name lebt nur noch als Alias ([[STATUS_MFU_ALT]],
 * [[kanonischerStatus]]). Der gleichnamige BEZIEHUNGSTYP „Mensch fürs
 * Unternehmen" ist davon nicht betroffen.
 */
export const STATUS_MFU = "Verantwortung fürs Unternehmen";
/** Früherer Speicherwert von [[STATUS_MFU]] (bis 29.09.2026). */
export const STATUS_MFU_ALT = "Mensch fürs Unternehmen";
/**
 * Wer dort ARBEITET, ohne für die Firma einzustehen — eine eigene Kategorie,
 * nicht die Kurzform von [[STATUS_MFU]] (PO 09.09.2026).
 *
 * Die Katalogzeile existiert seit Migration 202606100001 und wird hier nur
 * erstmals ANGEBOTEN; deshalb braucht dieser Schritt keine Migration.
 */
export const STATUS_MENSCH_IM_UNTERNEHMEN = "Mensch im Unternehmen";
/**
 * Derselbe Wert wie [[STATUS_MFU]] — bis 29.09.2026 ein eigener, zurückgezogener
 * Eintrag; heute nur noch ein zweiter Name für dieselbe Konstante.
 */
export const STATUS_VERANTWORTUNG = "Verantwortung fürs Unternehmen";

/** Alte Statusnamen → heutiger Name. Gegenstück zu `STATUS_ALIASE` im Backend. */
export const STATUS_ALIASE: Readonly<Record<string, string>> = {
  [STATUS_MFU_ALT]: STATUS_MFU,
};

/**
 * Heutiger Name eines Status (getrimmt, Alias aufgelöst). Für alles, was einen
 * Statusnamen von außen liest — Kontaktdaten aus einem alten Cache, gespeicherte
 * Filter im Browser.
 */
export const kanonischerStatus = (name: string): string => {
  const n = (name ?? "").trim();
  return STATUS_ALIASE[n] ?? n;
};
/** Neu 09/2026. Bewusst NICHT „Haushaltsmitglied" — den setzt die Haushaltsbereinigung. */
export const STATUS_BEZIEHUNG = "Kontakt mit Beziehung zu einem Kontakt";
/** Neu 09/2026. */
export const STATUS_MITARBEITER_COURTE = "Mitarbeiter Courté";
/**
 * Seit 11.09.2026 auch in der Anlage wählbar (User-Auftrag).
 *
 * Die Katalogzeile ist alt — bisher entstand sie ausschliesslich in der
 * Haushaltsbereinigung. Sie meint etwas anderes als [[STATUS_BEZIEHUNG]]:
 * hier lebt jemand im selben Haushalt, dort besteht irgendeine Beziehung.
 */
export const STATUS_HAUSHALTSMITGLIED = "Haushaltsmitglied";
/** Neu 09/2026. */
export const STATUS_LIEFERANT = "Lieferant";
/** Neu 09/2026. */
export const STATUS_SPONSORING = "Sponsoring Partner";
/** Neu 09/2026. Schrägstrich wie in der Notiz — anders als „Vermittler, Tippgeber". */
export const STATUS_MITGLIEDSCHAFT = "Mitgliedschaft/Verband";
/** Neu 09/2026. */
export const STATUS_DIREKTIONSPARTNER = "Direktionspartner";

// ── Auswahlliste des Privatkontakts (Auftrag vom 14.09.2026) ───────────────
// Die Personen-Liste trug bis dahin das Vokabular vom 09.09.2026. Sechs
// Einträge kamen dazu; fünf davon brauchten neue Katalogzeilen
// (Migration 202609140002).

/** Wer einmal Kunde war. Im Segment der Anlage steht er nicht — hier schon. */
export const STATUS_ALTKUNDE = "Altkunde";
/** Das Gegenstück: ein Interessent, aus dem nichts geworden ist. */
export const STATUS_ALTINTERESSENT = "Altinteressent";
/**
 * Lieferant oder Dienstleister AUS DER WELT DES KUNDEN.
 *
 * Bewusst ein eigener Wert und NICHT [[STATUS_LIEFERANT]]: Jener steht in
 * `CO_ARTEN` (`apps/api/src/coIntern.ts`) und meint einen Lieferanten VON
 * COURTÉ. Wer ihn setzt, macht den Kontakt zu einer courté-eigenen
 * Geschäftsbeziehung — unsichtbar für jeden ohne `cointern:read`, auch für
 * den, der ihn gerade angelegt hat.
 */
export const STATUS_LIEFERANT_DIENSTLEISTER = "Lieferant/Dienstleister";
/** Eigentümer, Mieter, Verwalter, Nießbrauchberechtigter … zu einer Immobilie. */
export const STATUS_IMMOBILIENBEZOGEN = "Immobilienbezogener Kontakt";
/** Wer spendet oder sponsert. Nicht [[STATUS_SPONSORING]] — siehe oben, CO-intern. */
export const STATUS_SPONSOR_SPENDER = "Sponsor/Spender";
/** Verein oder Verband. Nicht [[STATUS_MITGLIEDSCHAFT]] — siehe oben, CO-intern. */
export const STATUS_VEREIN_VERBAND = "Verein/Verband";

export type SonstigeStatusOption = {
  value: string;
  label: string;
};

/**
 * Die drei Hauptstatus der Anlage. Sie standen bis 09/2026 doppelt
 * hartkodiert in `NewPrivateContactForm` und `NewCompanyContactForm`.
 *
 * „Persönlicher Kontakt" steht bewusst NICHT in DIESER Liste: er ist kein
 * `ContactStatus`, sondern die zweite Achse (Sichtbarkeit) — ein `statusName`
 * dieses Namens fände keine Katalogzeile und würde ERSATZLOS verworfen.
 * Die Anlage-Masken zeigen beide Achsen seit 09/2026 trotzdem in EINEM
 * Segment; diese Projektion baut `data/kontaktstatusSegment.ts`, und die drei
 * Werte hier sind zugleich die Auswahl ihres zweiten Segments.
 */
export const HAUPT_STATUS_OPTIONS: string[] = ["Interessent", "Kunde", "Sonstige"];

/**
 * Die beiden Provisions-Status, in der Reihenfolge, in der sie der Fachbereich
 * nennt. Der Klammerzusatz ist Teil der Beschriftung, damit die Unterscheidung
 * schon beim Aufklappen sichtbar ist.
 */
export const PROVISIONS_STATUS_OPTIONS: SonstigeStatusOption[] = [
  { value: STATUS_VERMITTLER, label: `${STATUS_VERMITTLER} (kann Provision erhalten)` },
  { value: STATUS_EMPFEHLUNGSGEBER, label: `${STATUS_EMPFEHLUNGSGEBER} (keine Provision)` },
];

/**
 * Unterkategorien für eine PERSON — die Liste vom 14.09.2026.
 *
 * Sie folgt der Aufzählung des Users. Zwei seiner Einträge sind in der
 * Oberfläche keine Zeile, sondern eine ÜBERSCHRIFT, weil dahinter mehrere
 * Werte liegen (`data/rollenKatalog.ts`):
 *
 *   „Beziehung zu Privatkontakten (im und außerhalb des Haushaltes)"
 *        → Kontakt mit Beziehung zu einem Kontakt · Haushaltsmitglied
 *   „Unternehmens-Verbindung"
 *        → Mensch im Unternehmen (Arbeitnehmer) · Verantwortung fürs
 *          Unternehmen
 *
 * „Schaden-Beteiligter" ist ebenfalls eine Überschrift: die Einzelrollen
 * stehen seit dem 11.09.2026 unter „Rolle im Schaden".
 *
 * `STATUS_AP_PRODUKTPARTNER` bleibt zwingend enthalten — an genau diesem Wert
 * hängt der komplette Produktpartner-Weg.
 */
export const SONSTIGE_OPTIONS_PERSON: SonstigeStatusOption[] = [
  // Beziehung zu Privatkontakten
  { value: STATUS_BEZIEHUNG, label: "Beziehung außerhalb des Haushaltes" },
  { value: STATUS_HAUSHALTSMITGLIED, label: "Im selben Haushalt" },
  // Unternehmens-Verbindung
  { value: STATUS_MENSCH_IM_UNTERNEHMEN, label: `${STATUS_MENSCH_IM_UNTERNEHMEN} (Arbeitnehmer)` },
  { value: STATUS_MFU, label: STATUS_MFU },
  // Verbindung zu Courté
  ...PROVISIONS_STATUS_OPTIONS,
  { value: STATUS_AP_PRODUKTPARTNER, label: STATUS_AP_PRODUKTPARTNER },
  { value: STATUS_AP_KOOPERATIONSPARTNER, label: STATUS_AP_KOOPERATIONSPARTNER },
  { value: STATUS_ALTKUNDE, label: STATUS_ALTKUNDE },
  { value: STATUS_ALTINTERESSENT, label: STATUS_ALTINTERESSENT },
  { value: STATUS_SPONSOR_SPENDER, label: STATUS_SPONSOR_SPENDER },
  { value: STATUS_MITARBEITER_COURTE, label: STATUS_MITARBEITER_COURTE },
  // Umfeld des Kunden
  { value: STATUS_LIEFERANT_DIENSTLEISTER, label: STATUS_LIEFERANT_DIENSTLEISTER },
  { value: STATUS_IMMOBILIENBEZOGEN, label: STATUS_IMMOBILIENBEZOGEN },
  { value: STATUS_VEREIN_VERBAND, label: `${STATUS_VEREIN_VERBAND} (Mitgliedschaft/Verantwortung)` },
];

/**
 * Die vier courté-eigenen Geschäftsbeziehungen („CO intern", PO 09.09.2026).
 *
 * Sie stehen in der Auswahl nur für Nutzer MIT `cointern:read` — siehe
 * `sonstigeOptionenFirma`. Seit 09/2026 hat die Wahl eine Folge: Der Kontakt
 * gilt damit als CO-intern und verschwindet für alle anderen aus Liste, Suche
 * und Detailansicht. Ein Mitarbeiter ohne das Recht hätte sich mit diesem
 * Klick selbst aus dem gerade angelegten Kontakt ausgesperrt (HTTP 403 beim
 * Öffnen).
 */
export const CO_INTERN_STATUS: string[] = [
  STATUS_LIEFERANT,
  STATUS_SPONSORING,
  STATUS_MITGLIEDSCHAFT,
  STATUS_DIREKTIONSPARTNER,
];

/** Unterkategorien für eine FIRMA, in der Reihenfolge der Notiz. */
export const SONSTIGE_OPTIONS_FIRMA: SonstigeStatusOption[] = [
  ...PROVISIONS_STATUS_OPTIONS,
  { value: STATUS_PRODUKTPARTNER, label: STATUS_PRODUKTPARTNER },
  { value: STATUS_KOOPERATIONSPARTNER, label: STATUS_KOOPERATIONSPARTNER },
  { value: STATUS_LIEFERANT, label: STATUS_LIEFERANT },
  { value: STATUS_SPONSORING, label: STATUS_SPONSORING },
  { value: STATUS_MITGLIEDSCHAFT, label: STATUS_MITGLIEDSCHAFT },
  { value: STATUS_DIREKTIONSPARTNER, label: STATUS_DIREKTIONSPARTNER },
];

/**
 * Die Firmen-Unterkategorien für DIESEN Nutzer.
 *
 * Ohne `cointern:read` fehlen die vier CO-Arten. Das ist keine Kosmetik: Die
 * Wahl macht den Kontakt CO-intern, und der Anleger käme anschliessend nicht
 * mehr an ihn heran. Wer CO-Kontakte pflegen darf, bekommt sie wie bisher.
 */
export const sonstigeOptionenFirma = (darfCoIntern: boolean): SonstigeStatusOption[] =>
  darfCoIntern
    ? SONSTIGE_OPTIONS_FIRMA
    : SONSTIGE_OPTIONS_FIRMA.filter((o) => !CO_INTERN_STATUS.includes(o.value));
